import React, { useState, useEffect, useRef } from 'react';
import {
  PlusOutlined,
  PlusCircleOutlined,
  DeleteOutlined,
  HolderOutlined,
  AppstoreOutlined,
  InfoCircleOutlined,
  EnterOutlined,
  SoundOutlined,
  LoadingOutlined,
  PauseOutlined,
  PaperClipOutlined,
  EyeOutlined,
  CloseOutlined,
  VideoCameraOutlined,
  FilePdfOutlined,
  FilePdfFilled,
} from '@ant-design/icons';
import { Tooltip, message } from 'antd';
import { ThemeModal } from '../../pages/Styles/Theme';
import axios from 'axios';
import config from '../../config';
import { getUserToken } from '../../utils/helpers';
import { uploadSingleFile, generateDownloadUrl, generateDownloadUrlByKey } from '../../services/fileUploadService';
import { validateMediaFile, getMediaType, formatFileSize } from '../../utils/fileValidation';
import {
  DialogueWrapper,
  DialogueHeader,
  DialogueHeaderLeft,
  DialogueHeaderRight,
  DialogueBody,
  DialogueRow,
  DragHandle,
  DialogueNumber,
  DialogueInput,
  TimeInputWrapper,
  TimeInput,
  TimeUnit,
  ActionButton,
  AddButton,
  AddDialogueButton,
  DialogueFooter,
  QuestionWrapper,
  FollowupContainer,
  FollowupRow,
  FollowupLabel,
  PlayButton,
  QuestionContentWrapper,
  MediaPreviewInline,
  MediaThumbInline,
  MediaInfoInline,
  MediaActionBtn,
} from './QuestionBuilderStyle';

const escapeXmlAttr = (str) => {
  if (!str) return '';
  return str.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
};

const unescapeXmlAttr = (str) => {
  if (!str) return '';
  return str.replace(/&amp;/g, '&').replace(/&quot;/g, '"').replace(/&lt;/g, '<').replace(/&gt;/g, '>');
};

const parseMediaAttrs = (attrStr) => {
  if (!attrStr || !attrStr.includes('has_media="true"')) return null;
  const get = (name) => {
    const m = attrStr.match(new RegExp(`${name}="([^"]*)"`));
    return m ? m[1] : '';
  };
  return {
    media_type: get('media_type'),
    file_id: get('media_id'),
    file_key: get('media_file_key'),
    url: unescapeXmlAttr(get('media_url')),
    filename: unescapeXmlAttr(get('media_filename')),
  };
};

const buildMediaXmlAttrs = (media) => {
  if (!media || !media.media_type) return '';
  return ` has_media="true" media_type="${media.media_type}" media_id="${media.file_id || ''}" media_file_key="${media.file_key || ''}" media_url="${escapeXmlAttr(media.url || '')}" media_filename="${escapeXmlAttr(media.filename || '')}"`;
};

const getMediaTypeLabel = (type) => {
  if (type === 'image') return 'Image';
  if (type === 'video') return 'Video';
  if (type === 'pdf') return 'PDF';
  return '';
};

const QuestionBuilder = ({ value, onChange, globalDuration = 4.0, canEditDuration = true, onDurationChange, voiceId, onPlayStart, stopAudioRef }) => {
  const [questions, setQuestions] = useState([]);
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [draggedFollowup, setDraggedFollowup] = useState(null);
  const [isXmlMode, setIsXmlMode] = useState(false);
  const [xmlText, setXmlText] = useState('');
  const isInternalUpdateRef = useRef(false);
  const [playingId, setPlayingId] = useState(null);
  const [loadingId, setLoadingId] = useState(null);
  const audioRef = useRef(null);
  const mediaInputRef = useRef(null);
  const [mediaTarget, setMediaTarget] = useState(null);
  const [uploadingMediaId, setUploadingMediaId] = useState(null);
  const [mediaModalVisible, setMediaModalVisible] = useState(false);
  const [mediaModalData, setMediaModalData] = useState(null);

  const handlePlayTTS = async (id, text) => {
    if (!text || !text.trim()) {
      message.warning('No text to play');
      return;
    }

    // If same item is playing, stop it
    if (playingId === id) {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
      setPlayingId(null);
      return;
    }

    // Stop any currently playing audio
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    setPlayingId(null);

    try {
      setLoadingId(id);
      // Stop voice preview audio in parent
      onPlayStart?.();
      const response = await axios.post(
        `${config.api.base_url}/polling-agents/tts`,
        { voice_id: voiceId || 'kVYakPWKGwV1guNlg6lU', text: text.trim() },
        {
          headers: { Authorization: 'Bearer ' + getUserToken() },
          responseType: 'blob',
        }
      );

      const audioUrl = URL.createObjectURL(response.data);
      const audio = new Audio(audioUrl);
      audioRef.current = audio;

      audio.onended = () => {
        setPlayingId(null);
        audioRef.current = null;
        URL.revokeObjectURL(audioUrl);
      };

      audio.onerror = () => {
        setPlayingId(null);
        audioRef.current = null;
        URL.revokeObjectURL(audioUrl);
        message.error('Failed to play audio');
      };

      await audio.play();
      setPlayingId(id);
    } catch (error) {
      message.error('Failed to generate speech');
    } finally {
      setLoadingId(null);
    }
  };

  // Register stop function on parent ref so voice preview can stop our audio
  useEffect(() => {
    if (stopAudioRef) {
      stopAudioRef.current = () => {
        if (audioRef.current) {
          audioRef.current.pause();
          audioRef.current = null;
        }
        setPlayingId(null);
        setLoadingId(null);
      };
    }
    return () => {
      if (stopAudioRef) stopAudioRef.current = null;
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, [stopAudioRef]);

  const handleAttachMedia = (questionId, followupId = null) => {
    setMediaTarget({ questionId, followupId });
    mediaInputRef.current?.click();
  };

  const handleMediaFileChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !mediaTarget) return;

    const validation = validateMediaFile(file);
    if (!validation.valid) {
      message.error(validation.error);
      return;
    }

    const targetId = mediaTarget.followupId || mediaTarget.questionId;
    setUploadingMediaId(targetId);

    try {
      const result = await uploadSingleFile(file, 'polling-agent-media', null);
      const downloadRes = await generateDownloadUrl(result.fileId);

      const mediaData = {
        media_type: getMediaType(file.name),
        url: downloadRes?.data?.downloadUrl || '',
        filename: file.name,
        size: file.size,
        mimetype: file.type,
        file_id: result.fileId,
        file_key: result.fileKey,
      };

      let updated;
      if (mediaTarget.followupId) {
        updated = questions.map(q => {
          if (q.id === mediaTarget.questionId) {
            return {
              ...q,
              followups: (q.followups || []).map(f =>
                f.id === mediaTarget.followupId ? { ...f, media: mediaData } : f
              ),
            };
          }
          return q;
        });
      } else {
        updated = questions.map(q =>
          q.id === mediaTarget.questionId ? { ...q, media: mediaData } : q
        );
      }

      setQuestions(updated);
      isInternalUpdateRef.current = true;
      onChange?.(composeQuestionsToText(updated));
    } catch {
      message.error('Failed to upload media');
    } finally {
      setUploadingMediaId(null);
      setMediaTarget(null);
    }
  };

  const handleRemoveQuestionMedia = (questionId) => {
    const updated = questions.map(q =>
      q.id === questionId ? { ...q, media: undefined } : q
    );
    setQuestions(updated);
    isInternalUpdateRef.current = true;
    onChange?.(composeQuestionsToText(updated));
  };

  const handleRemoveFollowupMedia = (questionId, followupId) => {
    const updated = questions.map(q => {
      if (q.id === questionId) {
        return {
          ...q,
          followups: (q.followups || []).map(f =>
            f.id === followupId ? { ...f, media: undefined } : f
          ),
        };
      }
      return q;
    });
    setQuestions(updated);
    isInternalUpdateRef.current = true;
    onChange?.(composeQuestionsToText(updated));
  };

  const openMediaModal = (media) => {
    setMediaModalData(media);
    setMediaModalVisible(true);
  };

  useEffect(() => {
    if (isInternalUpdateRef.current) {
      isInternalUpdateRef.current = false;
      return;
    }

    if (value) {
      const parsed = parseQuestionsFromText(value);
      setQuestions(parsed);
      setXmlText(value);
    } else {
      setQuestions([]);
      setXmlText('');
    }
  }, [value]);

  // Auto-generate download URLs for media items that have file_key but no url
  useEffect(() => {
    let cancelled = false;
    const generateMissingUrls = async () => {
      const needsUrl = [];
      questions.forEach((q, qi) => {
        if (q.media?.file_key && !q.media.url) needsUrl.push({ qi, fi: null, fileKey: q.media.file_key });
        (q.followups || []).forEach((f, fi) => {
          if (f.media?.file_key && !f.media.url) needsUrl.push({ qi, fi, fileKey: f.media.file_key });
        });
      });
      if (needsUrl.length === 0) return;

      const urlMap = {};
      await Promise.all(needsUrl.map(async (item) => {
        try {
          const res = await generateDownloadUrlByKey(item.fileKey);
          if (res?.data?.downloadUrl) urlMap[item.fileKey] = res.data.downloadUrl;
        } catch {}
      }));

      if (cancelled || Object.keys(urlMap).length === 0) return;

      setQuestions(prev => {
        const updated = prev.map(q => {
          const qMedia = q.media?.file_key && urlMap[q.media.file_key]
            ? { ...q.media, url: urlMap[q.media.file_key] } : q.media;
          const followups = (q.followups || []).map(f => {
            if (f.media?.file_key && urlMap[f.media.file_key]) {
              return { ...f, media: { ...f.media, url: urlMap[f.media.file_key] } };
            }
            return f;
          });
          return { ...q, media: qMedia, followups };
        });
        return updated;
      });
    };
    generateMissingUrls();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questions.reduce((acc, q) => {
    if (q.media?.file_key && !q.media.url) acc += q.media.file_key;
    (q.followups || []).forEach(f => { if (f.media?.file_key && !f.media.url) acc += f.media.file_key; });
    return acc;
  }, '')]);

  // Update all questions' breakTime when globalDuration changes
  useEffect(() => {
    if (questions.length > 0 && globalDuration) {
      const updated = questions.map(q => ({
        ...q,
        breakTime: globalDuration,
        followups: q.followups || []
      }));
      setQuestions(updated);
      isInternalUpdateRef.current = true;
      const composed = composeQuestionsToText(updated);
      onChange?.(composed);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [globalDuration]);

  const parseQuestionsFromText = (text) => {
    if (!text || !text.trim()) return [];

    if (/<question[\s>]/.test(text)) {
      return parseNewFormat(text);
    }

    const lines = text.split('\n').filter(line => line.trim());
    const parsed = [];
    let currentText = '';

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].trim();
      if (line.match(/^<break\s+time="([\d.]+)s"\s*\/?>$/)) {
        if (currentText) {
          parsed.push({ id: Date.now() + Math.random(), text: currentText.trim(), breakTime: globalDuration, followups: [] });
          currentText = '';
        }
      } else if (!line.startsWith('<') && !line.endsWith('>')) {
        currentText = currentText ? currentText + ' ' + line : line;
      }
    }

    if (currentText.trim()) {
      parsed.push({ id: Date.now() + Math.random(), text: currentText.trim(), breakTime: globalDuration, followups: [] });
    }

    return parsed;
  };

  const parseNewFormat = (text) => {
    const parsed = [];
    const questionRegex = /<question([^>]*)>([\s\S]*?)<\/question>/g;
    let match;

    while ((match = questionRegex.exec(text)) !== null) {
      const attrs = match[1];
      const content = match[2];
      let qText = '';
      for (const line of content.split('\n')) {
        const t = line.trim();
        if (t.startsWith('<break') || t.startsWith('<followup')) break;
        if (t && !t.startsWith('<')) qText += (qText ? ' ' : '') + t;
      }

      const qMedia = parseMediaAttrs(attrs);

      const followups = [];
      const fRegex = /<followup([^>]*)>([\s\S]*?)<\/followup>/g;
      let fMatch;
      while ((fMatch = fRegex.exec(content)) !== null) {
        const fAttrs = fMatch[1];
        const fText = fMatch[2].trim().replace(/\s+/g, ' ');
        const fMedia = parseMediaAttrs(fAttrs);
        if (fText) followups.push({ id: Date.now() + Math.random(), text: fText, ...(fMedia ? { media: fMedia } : {}) });
      }

      if (qText.trim()) {
        parsed.push({
          id: Date.now() + Math.random(),
          text: qText.trim(),
          breakTime: globalDuration,
          followups,
          ...(qMedia ? { media: qMedia } : {}),
        });
      }
    }
    return parsed;
  };

  const composeQuestionsToText = (questionsList) => {
    return questionsList.map(q => {
      const qMediaAttrs = buildMediaXmlAttrs(q.media);
      let c = `<question${qMediaAttrs}>\n${q.text.trim()}\n<break time="${globalDuration}s"/>\n`;
      if (q.followups?.length > 0) {
        q.followups.forEach(f => {
          const fMediaAttrs = buildMediaXmlAttrs(f.media);
          c += `<followup${fMediaAttrs}>\n${f.text.trim()}\n</followup>\n<break time="${globalDuration}s"/>\n`;
        });
      }
      return c + '</question>';
    }).join('\n');
  };

  const handleQuestionChange = (id, field, newValue) => {
    const updated = questions.map(q =>
      q.id === id ? { ...q, [field]: newValue } : q
    );
    setQuestions(updated);
    isInternalUpdateRef.current = true;
    const composed = composeQuestionsToText(updated);
    onChange?.(composed);
  };

  const handleAddQuestion = () => {
    const newQuestion = { id: Date.now() + Math.random(), text: '', breakTime: globalDuration, followups: [] };
    const updated = [...questions, newQuestion];
    setQuestions(updated);
    isInternalUpdateRef.current = true;
    onChange?.(composeQuestionsToText(updated));
  };

  const handleAddFollowup = (questionId) => {
    const updated = questions.map(q => q.id === questionId
      ? { ...q, followups: [...(q.followups || []), { id: Date.now() + Math.random(), text: '' }] }
      : q);
    setQuestions(updated);
    isInternalUpdateRef.current = true;
    onChange?.(composeQuestionsToText(updated));
  };

  const handleInsertFollowupAfter = (questionId, fIndex) => {
    const updated = questions.map(q => {
      if (q.id === questionId) {
        const f = [...(q.followups || [])];
        f.splice(fIndex + 1, 0, { id: Date.now() + Math.random(), text: '' });
        return { ...q, followups: f };
      }
      return q;
    });
    setQuestions(updated);
    isInternalUpdateRef.current = true;
    onChange?.(composeQuestionsToText(updated));
  };

  const handleFollowupChange = (questionId, followupId, newText) => {
    const updated = questions.map(q => q.id === questionId
      ? { ...q, followups: (q.followups || []).map(f => f.id === followupId ? { ...f, text: newText } : f) }
      : q);
    setQuestions(updated);
    isInternalUpdateRef.current = true;
    onChange?.(composeQuestionsToText(updated));
  };

  const handleDeleteFollowup = (questionId, followupId) => {
    const updated = questions.map(q => q.id === questionId
      ? { ...q, followups: (q.followups || []).filter(f => f.id !== followupId) }
      : q);
    setQuestions(updated);
    isInternalUpdateRef.current = true;
    onChange?.(composeQuestionsToText(updated));
  };

  const handleFollowupDragStart = (e, questionId, fIndex) => {
    setDraggedFollowup({ questionId, fIndex });
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleFollowupDragOver = (e, questionId, targetIndex) => {
    e.preventDefault();
    if (!draggedFollowup || draggedFollowup.questionId !== questionId || draggedFollowup.fIndex === targetIndex) return;
    const updated = questions.map(q => {
      if (q.id === questionId) {
        const f = [...(q.followups || [])];
        const item = f[draggedFollowup.fIndex];
        f.splice(draggedFollowup.fIndex, 1);
        f.splice(targetIndex, 0, item);
        return { ...q, followups: f };
      }
      return q;
    });
    setQuestions(updated);
    setDraggedFollowup({ questionId, fIndex: targetIndex });
  };

  const handleFollowupDragEnd = () => {
    setDraggedFollowup(null);
    isInternalUpdateRef.current = true;
    onChange?.(composeQuestionsToText(questions));
  };

  const handleDeleteQuestion = (id) => {
    const updated = questions.filter(q => q.id !== id);
    setQuestions(updated);
    isInternalUpdateRef.current = true;
    const composed = composeQuestionsToText(updated);
    onChange?.(composed);
  };

  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;

    const updated = [...questions];
    const draggedItem = updated[draggedIndex];
    updated.splice(draggedIndex, 1);
    updated.splice(index, 0, draggedItem);

    setQuestions(updated);
    setDraggedIndex(index);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
    isInternalUpdateRef.current = true;
    const composed = composeQuestionsToText(questions);
    onChange?.(composed);
  };

  const handleInsertAfter = (e, index) => {
    e.stopPropagation();
    e.preventDefault();
    const newQuestion = { id: Date.now() + Math.random(), text: '', breakTime: globalDuration, followups: [] };
    const updated = [...questions.slice(0, index + 1), newQuestion, ...questions.slice(index + 1)];
    setQuestions(updated);
    isInternalUpdateRef.current = true;
    onChange?.(composeQuestionsToText(updated));
  };

  const handleXmlTextChange = (e) => {
    const newText = e.target.value;
    setXmlText(newText);
    isInternalUpdateRef.current = true;
    onChange?.(newText);
  };

  const handleModeToggle = (checked) => {
    if (checked) {
      const composed = composeQuestionsToText(questions);
      setXmlText(composed);
    } else {
      const parsed = parseQuestionsFromText(xmlText);
      setQuestions(parsed);
    }
    setIsXmlMode(checked);
  };

  return (
    <DialogueWrapper>
      {/* Header - XML toggle hidden */}
      <DialogueHeader>
        <DialogueHeaderLeft>
          <AppstoreOutlined />
          <span>Visual Builder Mode</span>
        </DialogueHeaderLeft>
        {canEditDuration && (
          <DialogueHeaderRight>
            <span>Break Duration</span>
            <Tooltip title="Pause duration between each question">
              <InfoCircleOutlined style={{ fontSize: 12, color: '#8893a1', cursor: 'help' }} />
            </Tooltip>
            <TimeInputWrapper style={{ width: 'auto', margin: 0 }}>
              <TimeInput
                value={globalDuration}
                onChange={(val) => {
                  if (onDurationChange) {
                    onDurationChange(val || 4.0);
                  }
                }}
                min={0}
                max={10}
                step={0.5}
                precision={1}
              />
              <TimeUnit>Sec</TimeUnit>
            </TimeInputWrapper>
          </DialogueHeaderRight>
        )}
      </DialogueHeader>

      {/* Always show Visual Builder Mode */}
      <>
        <DialogueBody>
            {questions.map((question, index) => (
              <QuestionWrapper key={question.id}>
                <DialogueRow
                  $isDragging={draggedIndex === index}
                  onDragOver={(e) => handleDragOver(e, index)}
                >
                  <DragHandle
                    className='dragHandle'
                    draggable
                    onDragStart={(e) => handleDragStart(e, index)}
                    onDragEnd={handleDragEnd}
                  >
                    <HolderOutlined />
                  </DragHandle>

                  <DialogueNumber>Q{index + 1}</DialogueNumber>

                  <QuestionContentWrapper>
                    <DialogueInput
                      className='dialogueInput'
                      value={question.text}
                      onChange={(e) => handleQuestionChange(question.id, 'text', e.target.value)}
                      placeholder="Enter dialogue or question..."
                    />
                    {question.media && (
                      <MediaPreviewInline>
                        <MediaThumbInline>
                          {question.media.media_type === 'image' && question.media.url ? (
                            <img src={question.media.url} alt="" />
                          ) : question.media.media_type === 'video' ? (
                            <VideoCameraOutlined />
                          ) : question.media.media_type === 'pdf' ? (
                            <FilePdfOutlined />
                          ) : (
                            <PaperClipOutlined />
                          )}
                        </MediaThumbInline>
                        <MediaInfoInline>
                          <div className="media-filename">{question.media.filename}</div>
                          <div className="media-meta">{getMediaTypeLabel(question.media.media_type)}{question.media.size ? ` · ${formatFileSize(question.media.size)}` : ''}</div>
                        </MediaInfoInline>
                        {question.media.url && (
                          <Tooltip title="View media">
                            <MediaActionBtn onClick={() => openMediaModal(question.media)}><EyeOutlined /></MediaActionBtn>
                          </Tooltip>
                        )}
                        <Tooltip title="Remove media">
                          <MediaActionBtn $danger onClick={() => handleRemoveQuestionMedia(question.id)}><CloseOutlined /></MediaActionBtn>
                        </Tooltip>
                      </MediaPreviewInline>
                    )}
                  </QuestionContentWrapper>

                  <div className='actionButtonsWrapper'>
                    <Tooltip title={uploadingMediaId === question.id ? "Uploading..." : question.media ? "Replace media" : "Attach media"}>
                      <AddButton
                        className='actionButton'
                        type="text"
                        icon={uploadingMediaId === question.id ? <LoadingOutlined spin /> : <PaperClipOutlined />}
                        onClick={() => handleAttachMedia(question.id)}
                        disabled={!!uploadingMediaId}
                      />
                    </Tooltip>
                    <Tooltip title={playingId === question.id ? "Stop" : "Play question"}>
                      <PlayButton
                        className='actionButton'
                        type="text"
                        icon={loadingId === question.id ? <LoadingOutlined spin /> : playingId === question.id ? <PauseOutlined /> : <SoundOutlined />}
                        onClick={() => handlePlayTTS(question.id, question.text)}
                        disabled={loadingId === question.id}
                      />
                    </Tooltip>
                    <Tooltip title="Delete question">
                      <ActionButton
                        className='actionButton'
                        type="text"
                        icon={<DeleteOutlined />}
                        onClick={() => handleDeleteQuestion(question.id)}
                      />
                    </Tooltip>
                    <Tooltip title="Insert question below">
                      <AddButton
                        className='actionButton'
                        type="text"
                        icon={<PlusCircleOutlined />}
                        onClick={(e) => handleInsertAfter(e, index)}
                      />
                    </Tooltip>
                    <Tooltip title="Add Follow-up">
                      <AddButton
                        className='actionButton'
                        type="text"
                        icon={<EnterOutlined style={{ fontSize: 14 }} />}
                        onClick={() => handleAddFollowup(question.id)}
                      />
                    </Tooltip>
                  </div>
                </DialogueRow>

                {question.followups?.length > 0 && (
                  <FollowupContainer>
                    {question.followups.map((followup, fIndex) => (
                      <FollowupRow
                        key={followup.id}
                        $isDragging={draggedFollowup?.questionId === question.id && draggedFollowup?.fIndex === fIndex}
                        onDragOver={(e) => handleFollowupDragOver(e, question.id, fIndex)}
                      >
                        <DragHandle
                          className='dragHandle'
                          draggable
                          onDragStart={(e) => handleFollowupDragStart(e, question.id, fIndex)}
                          onDragEnd={handleFollowupDragEnd}
                        >
                          <HolderOutlined />
                        </DragHandle>
                        <FollowupLabel>F{fIndex + 1}</FollowupLabel>
                        <QuestionContentWrapper>
                          <DialogueInput
                            className='dialogueInput'
                            value={followup.text}
                            onChange={(e) => handleFollowupChange(question.id, followup.id, e.target.value)}
                            placeholder="Enter follow-up question..."
                          />
                          {followup.media && (
                            <MediaPreviewInline>
                              <MediaThumbInline>
                                {followup.media.media_type === 'image' && followup.media.url ? (
                                  <img src={followup.media.url} alt="" />
                                ) : followup.media.media_type === 'video' ? (
                                  <VideoCameraOutlined />
                                ) : followup.media.media_type === 'pdf' ? (
                                  <FilePdfOutlined />
                                ) : (
                                  <PaperClipOutlined />
                                )}
                              </MediaThumbInline>
                              <MediaInfoInline>
                                <div className="media-filename">{followup.media.filename}</div>
                                <div className="media-meta">{getMediaTypeLabel(followup.media.media_type)}{followup.media.size ? ` · ${formatFileSize(followup.media.size)}` : ''}</div>
                              </MediaInfoInline>
                              {followup.media.url && (
                                <Tooltip title="View media">
                                  <MediaActionBtn onClick={() => openMediaModal(followup.media)}><EyeOutlined /></MediaActionBtn>
                                </Tooltip>
                              )}
                              <Tooltip title="Remove media">
                                <MediaActionBtn $danger onClick={() => handleRemoveFollowupMedia(question.id, followup.id)}><CloseOutlined /></MediaActionBtn>
                              </Tooltip>
                            </MediaPreviewInline>
                          )}
                        </QuestionContentWrapper>
                        <div className='actionButtonsWrapper'>
                          <Tooltip title={uploadingMediaId === followup.id ? "Uploading..." : followup.media ? "Replace media" : "Attach media"}>
                            <AddButton
                              className='actionButton'
                              type="text"
                              icon={uploadingMediaId === followup.id ? <LoadingOutlined spin /> : <PaperClipOutlined />}
                              onClick={() => handleAttachMedia(question.id, followup.id)}
                              disabled={!!uploadingMediaId}
                            />
                          </Tooltip>
                          <Tooltip title={playingId === followup.id ? "Stop" : "Play follow-up"}>
                            <PlayButton
                              className='actionButton'
                              type="text"
                              icon={loadingId === followup.id ? <LoadingOutlined spin /> : playingId === followup.id ? <PauseOutlined /> : <SoundOutlined />}
                              onClick={() => handlePlayTTS(followup.id, followup.text)}
                              disabled={loadingId === followup.id}
                            />
                          </Tooltip>
                          <Tooltip title="Delete follow-up">
                            <ActionButton
                              className='actionButton'
                              type="text"
                              icon={<DeleteOutlined />}
                              onClick={() => handleDeleteFollowup(question.id, followup.id)}
                            />
                          </Tooltip>
                          <Tooltip title="Add Follow-up Below">
                            <AddButton
                              className='actionButton'
                              type="text"
                              icon={<PlusCircleOutlined />}
                              onClick={() => handleInsertFollowupAfter(question.id, fIndex)}
                            />
                          </Tooltip>
                        </div>
                      </FollowupRow>
                    ))}
                  </FollowupContainer>
                )}
              </QuestionWrapper>
            ))}

            <AddDialogueButton onClick={handleAddQuestion}>
              <PlusOutlined />
              <span>Add Question</span>
            </AddDialogueButton>
          </DialogueBody>

          {/* Footer with count and duration */}
          <DialogueFooter>
            <span className="dialogue-count">
              {questions.length} Question{questions.length !== 1 ? 's' : ''}
            </span>
            <span className="dialogue-duration">
              Est. duration: <strong>{Math.min(60, Math.max(1, questions.length * 2))} min</strong>
            </span>
          </DialogueFooter>
        </>
      {/* Hidden file input for media upload */}
      <input
        ref={mediaInputRef}
        type="file"
        accept=".png,.jpg,.jpeg,.mp4,.pdf"
        style={{ display: 'none' }}
        onChange={handleMediaFileChange}
      />

      {/* Media preview modal */}
      <ThemeModal
        title="Media Preview"
        open={mediaModalVisible}
        onCancel={() => setMediaModalVisible(false)}
        footer={null}
        width={mediaModalData?.media_type === 'pdf' ? 680 : '70%'}
        centered
        destroyOnClose
      >
        {mediaModalData && !mediaModalData.url && (
          <div style={{ textAlign: 'center', padding: 40, color: '#8893a1', fontFamily: 'Montserrat, sans-serif' }}>
            Media preview available after saving
          </div>
        )}
        {mediaModalData?.media_type === 'image' && mediaModalData.url && (
          <img src={mediaModalData.url} alt={mediaModalData.filename} style={{ maxWidth: '100%', maxHeight: '80vh', objectFit: 'contain', display: 'block', margin: '0 auto' }} />
        )}
        {mediaModalData?.media_type === 'video' && mediaModalData.url && (
          <video src={mediaModalData.url} controls style={{ maxWidth: '100%', maxHeight: '80vh', display: 'block', margin: '0 auto' }} />
        )}
        {mediaModalData?.media_type === 'pdf' && mediaModalData.url && (
          <iframe src={`${mediaModalData.url}#toolbar=0&navpanes=0&view=FitH`} style={{ width: '100%', height: '80vh', border: 'none', background: '#fff' }} title={mediaModalData.filename} />
        )}
      </ThemeModal>
    </DialogueWrapper>
  );
};

export default QuestionBuilder;
